<template>
  <v-card class="secondary">
    <v-form ref="form" lazy-validation>
      <v-card-title class="font-weight-medium secondary--text primary py-2">
        <v-icon small color="secondary" class="mr-2"
          >mdi-bullhorn-outline</v-icon
        >
        {{ edit ? "Edit Announcement" : "Add Announcement" }}
        <v-spacer></v-spacer>
        <v-btn small icon @click="$emit('closeDialog')">
          <v-icon small color="secondary">mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />

      <v-card-text class="pt-4 pb-2">
        <v-row dense>
          <v-col cols="12">
            <v-text-field
              v-model.trim="form.title"
              label="Title*"
              outlined
              dense
              hide-details="auto"
              :rules="[rules.required]"
            />
          </v-col>
          <v-col cols="12">
            <v-textarea
              v-model.trim="form.content"
              label="Content*"
              outlined
              dense
              rows="3"
              hide-details="auto"
              :rules="[rules.required]"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="form.type"
              :items="typeOptions"
              item-text="text"
              item-value="value"
              label="Type*"
              outlined
              dense
              hide-details="auto"
              :rules="[rules.required]"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="form.priority"
              :items="priorityOptions"
              item-text="text"
              item-value="value"
              label="Priority"
              outlined
              dense
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-menu
              v-model="fromDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  :value="formattedFromDate"
                  label="From Date"
                  outlined
                  dense
                  readonly
                  clearable
                  hide-details="auto"
                  placeholder="dd-mm-yyyy"
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="form.from_date = null"
                />
              </template>
              <v-date-picker
                v-model="form.from_date"
                :min="today"
                @input="fromDateMenu = false"
              />
            </v-menu>
          </v-col>
          <v-col cols="12" md="6">
            <v-menu
              v-model="toDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  :value="formattedToDate"
                  label="To Date"
                  outlined
                  dense
                  readonly
                  clearable
                  hide-details="auto"
                  placeholder="dd-mm-yyyy"
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="form.to_date = null"
                />
              </template>
              <v-date-picker
                v-model="form.to_date"
                :min="form.from_date || today"
                @input="toDateMenu = false"
              />
            </v-menu>
          </v-col>

          <v-col v-if="!isMarquee" cols="12">
            <v-file-input
              v-model="newFiles"
              label="Attachments"
              outlined
              dense
              multiple
              small-chips
              show-size
              hide-details="auto"
              prepend-icon=""
              prepend-inner-icon="mdi-paperclip"
              :rules="[fileSizeRule]"
            />
          </v-col>

          <v-col
            v-if="!isMarquee && existingAttachments.length"
            cols="12"
            class="pt-0"
          >
            <div class="caption grey--text mb-1">Uploaded</div>
            <v-chip
              v-for="file in existingAttachments"
              :key="file.id"
              small
              class="mr-2 mb-1"
              close
              close-icon="mdi-close"
              @click="$emit('download', file)"
              @click:close="removeAttachment(file)"
            >
              <v-icon left x-small>mdi-file-outline</v-icon>
              {{ file.file_name }}
            </v-chip>
          </v-col>

          <v-col cols="12" class="py-0">
            <v-checkbox
              v-model="form.is_active"
              label="Active"
              dense
              hide-details="auto"
              class="mt-1"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="pt-0 pb-3 mx-2">
        <v-spacer></v-spacer>
        <v-btn text color="error" @click="$emit('closeDialog')">Cancel</v-btn>
        <v-btn class="primary" :loading="loading" @click="save">
          {{ edit ? "Update" : "Save" }}
        </v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script>
import { rules } from "@/helpers/rules";

const MAX_FILE_SIZE = 2 * 1024 * 1024;

export default {
  name: "AnnouncementForm",
  props: {
    announcement: {
      type: Object,
      default: () => ({}),
    },
    edit: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    typeOptions: {
      type: Array,
      default: () => [],
    },
    priorityOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      rules,
      fromDateMenu: false,
      toDateMenu: false,
      newFiles: [],
      existingAttachments: [],
      removedAttachmentIds: [],
      form: {
        title: "",
        content: "",
        from_date: null,
        to_date: null,
        is_active: true,
        type: "announcement",
        priority: 2,
      },
    };
  },
  computed: {
    isMarquee() {
      return this.form.type === "marquee";
    },
    today() {
      return this.localDateString(new Date());
    },
    formattedFromDate() {
      return this.displayDate(this.form.from_date);
    },
    formattedToDate() {
      return this.displayDate(this.form.to_date);
    },
  },
  watch: {
    isMarquee(value) {
      if (value) this.newFiles = [];
    },
  },
  created() {
    this.init();
  },
  methods: {
    init() {
      if (!this.edit) return;

      this.form = {
        ...this.form,
        ...this.announcement,
        from_date: this.toDateInput(this.announcement.from_date),
        to_date: this.toDateInput(this.announcement.to_date),
      };
      this.existingAttachments = [...(this.announcement.attachments || [])];
    },
    fileSizeRule(files) {
      const oversized = (files || []).find((f) => f.size > MAX_FILE_SIZE);
      return oversized ? `${oversized.name} exceeds 2 MB` : true;
    },
    removeAttachment(file) {
      this.existingAttachments = this.existingAttachments.filter(
        (a) => a.id !== file.id,
      );
      this.removedAttachmentIds.push(file.id);
    },
    // The API stores UTC instants; the picker works in the local calendar day.
    toDateInput(value) {
      return value ? this.localDateString(new Date(value)) : null;
    },
    localDateString(date) {
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${date.getFullYear()}-${month}-${day}`;
    },
    displayDate(value) {
      if (!value) return "";
      const [year, month, day] = value.split("-");
      return `${day}-${month}-${year}`;
    },

    toUtcDateTime(value, endOfDay) {
      if (!value) return null;
      const time = endOfDay ? "23:59:59" : "00:00:00";
      return new Date(`${value}T${time}`).toISOString();
    },
    save() {
      if (!this.$refs.form.validate()) return;

      const payload = {
        ...this.form,
        from_date: this.toUtcDateTime(this.form.from_date, false),
        to_date: this.toUtcDateTime(this.form.to_date, true),
      };

      delete payload.attachments;

      const attachments = {
        newFiles: this.newFiles || [],
        removedIds: this.removedAttachmentIds,
      };

      this.$emit(this.edit ? "update" : "create", payload, attachments);
    },
  },
};
</script>

<style scoped></style>
